import server from 'api';

// Client of /v03/ticketDoor (gastronaut-api serverV03/Routes/TicketDoor). The
// server is authoritative for every count, payment and permission; nothing
// here caches a positive result.

export type DoorPermissions = {
  admit: boolean;
  deny: boolean;
  recordPayment: boolean;
  admitUnpaid: boolean;
  lead: boolean;
};

export type DoorSession = {
  id: string;
  restaurantId: string;
  uid: string;
  signature: string;
  signatureName: string;
  eventId: string;
  date: string;
  expiresAt: number;
  groupId?: string;
};

export type DoorEventOption = {
  eventId: string;
  date: string;
  time: string;
  title: string;
  bookings: number;
  canonical: boolean;
  /** Entry closed by Schichtleitung (reversible); not offered for new door sessions. */
  closed: boolean;
};

/** A closed event day of any date, for reopening under Verwaltung. */
/** One page of the event list; `olderBefore` loads the next older days, null when history ends (one year). */
export type DoorEventPage = { events: DoorEventOption[]; olderBefore: string | null };

export type ClosedEvent ={ eventId: string; date: string; title: string; closedAt: number; closedBy: string };

export type DoorMe = { uid: string; permissions: DoorPermissions; isAdmin: boolean };

export type DoorPaymentKind = 'free' | 'paid' | 'deposit' | 'outstanding' | 'payAtVenue' | 'guestList';

export type DoorPayment = {
  kind: DoorPaymentKind;
  label: string;
  totalCents: number;
  paidCents: number;
  outstandingCents: number;
};

export type DoorActor = { uid: string; signature: string; signatureName: string };

export type DoorHistoryEntry = {
  id: string;
  type: 'admission' | 'undo' | 'denial' | 'legacy_release';
  quantity: number;
  actor: DoorActor;
  at: number;
  recordedAt: number;
  source: 'door' | 'manual-reconciliation' | 'table-plan';
  decision: 'standard' | 'unpaid' | 'payment' | null;
  reason: string | null;
  payment: { amountCents: number; method: 'cash' | 'card_terminal'; reference: string | null } | null;
  undoOf: string | null;
};

export type DoorView = {
  reservationId: string;
  ticketId: string | null;
  customId: string;
  guestName: string;
  date: string;
  time: string;
  eventId: string;
  eventTitle: string;
  valid: boolean;
  entryClosed?: boolean;
  problem: { code: string; message: string } | null;
  counts: { total: number; admitted: number; remaining: number };
  payment: DoorPayment;
  history: DoorHistoryEntry[];
  lastAdmission: DoorHistoryEntry | null;
  warning: { code: string; message: string } | null;
  reservationPath: string;
  kind?: 'ticket' | 'reservation';
  selections?: { kind: 'option' | 'addOn'; id: string; title: string; quantity: number; per: 'perPerson' | 'perGroup' | null }[];
};

export type RosterRow = {
  reservationId: string;
  ticketId: string | null;
  customId: string;
  name: string;
  email: string;
  phone: string;
  time: string;
  persons: number;
  admitted: number;
  remaining: number;
  payment: DoorPayment;
  problem: string | null;
  cancelled?: boolean;
  problemMessage?: string | null;
  kind?: 'ticket' | 'reservation';
  categoryTitle?: string;
  entryClosed?: boolean;
};

export type DoorPopulationCount = { persons: number; bookings: number };
export type DoorBreakdown = { tickets: DoorPopulationCount; namedReservations: DoorPopulationCount; clarification: DoorPopulationCount; cancelled: DoorPopulationCount; closed: DoorPopulationCount };
export type DoorList = {
  event: { eventId: string; date: string; title: string; closed: boolean };
  totals: { persons: number; admitted: number; remaining: number };
  rows: RosterRow[];
  bookings?: number;
  breakdown?: DoorBreakdown;
  categories?: { title: string; bookings: number; persons: number; admitted: number }[];
};

export type StaffDoorRights = {
  uid: string;
  name: string;
  email: string;
  admin: boolean;
  chefWaiter: boolean;
  ticketDoor: { recordPayment: boolean; admitUnpaid: boolean; lead: boolean };
  effective: DoorPermissions;
};

export type DeliveryStatus = 'not_sent' | 'sending' | 'stale_sending' | 'sent' | 'failed' | 'not_eligible';

export type DeliveryRecipient = {
  reservationId: string;
  /** The ticket's own category event (in an entry group: one of its categories). */
  eventId?: string;
  categoryTitle?: string;
  name: string;
  email: string;
  customId: string;
  persons: number;
  eligible: boolean;
  ineligibleReason: string | null;
  /** Staff PDF available (also without an e-mail address); absent from older APIs → `eligible`. */
  pdf?: boolean;
  status: DeliveryStatus;
  attempts: number;
  lastError: string | null;
  sentAt: number | null;
};

/** One category of the recipient list (a single event has one). */
export type DeliveryCategory = { eventId: string; title: string; offerQrCode: boolean };

export type DeliveryOverview = {
  /** At least one category offers QR tickets. */
  offerQrCode: boolean;
  eventSource: string | null;
  /** Absent from older APIs. */
  categories?: DeliveryCategory[];
  recipients: DeliveryRecipient[];
};

/** Recipients per send (the API refuses more). */
export const MAX_SEND_RECIPIENTS = 500;

export type DeliveryResult = {
  reservationId: string;
  status: 'sent' | 'failed' | 'skipped';
  reason: string | null;
};

export type DoorFailure = {
  status: number;
  code: string;
  message: string;
  /** No answer or a 5xx: the server may have saved the operation; only the same operation may be resent. */
  uncertain?: boolean;
  [key: string]: unknown;
};

const EXPECTED = [400, 401, 403, 404, 409];

/**
 * The server's refusal as data, or a lost answer (status 0): then it is
 * UNKNOWN whether the server saved the request; never shown as success.
 */
export const failureOf = (error: any): DoorFailure => {
  const response = error?.response;
  if (!response || response.status >= 500) {
    // A 5xx can come after the server committed (e.g. the answer failed later,
    // or a proxy in between): exactly as uncertain as no answer at all.
    return {
      status: response?.status ?? 0,
      code: response ? 'server_error' : 'network',
      uncertain: true,
      message: `${
        response ? `Serverfehler (${response.status})` : 'Keine Antwort vom Server'
      } – ob der Vorgang gespeichert wurde, ist unbekannt. Bitte „Erneut senden“: derselbe Vorgang wird nie doppelt gezählt.`,
    };
  }
  return {
    status: response.status,
    code: response.data?.code ?? 'error',
    message: response.data?.message ?? 'Unbekannter Fehler.',
    ...(response.data ?? {}),
  };
};

/** A lost or 5xx answer of a READ: nothing to resend, just try again. */
export const readFailure = (f: DoorFailure) =>
  f.uncertain ? `${f.status ? `Serverfehler (${f.status}).` : 'Keine Antwort vom Server.'} Bitte erneut versuchen.` : f.message;

const blobText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * With responseType 'blob' a refusal's JSON body (code, message) arrives as a Blob:
 * read it back so the server's reason reaches the user; anything else is said honestly.
 */
const withReadableBody = async (error: any) => {
  const response = error?.response;
  if (!response || !(response.data instanceof Blob)) return error;
  let body: any = null;
  try {
    body = JSON.parse(await blobText(response.data));
  } catch {
    body = null;
  }
  const data =
    body && typeof body.message === 'string' ? body : { code: 'error', message: `Fehler ${response.status} ohne Begründung vom Server.` };
  return { ...error, response: { ...response, data } };
};

const base = (restaurantId: string) => `/v03/ticketDoor/${encodeURIComponent(restaurantId)}`;

const headers = (session?: DoorSession | null) =>
  session ? { 'X-Door-Session': session.id } : undefined;

export const newOperationId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `op_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
};

export type DeliveryCounts = { total: number; sent: number; notSent: number; failed: number; sending: number; unavailable: number };
/** Why bookings cannot get a QR mail right now (summary counts per reason). */
export type UnavailableReason = 'app_disabled' | 'event_not_found' | 'qr_disabled' | 'no_email';
export type UnavailableReasons = Partial<Record<UnavailableReason | string, number>>;
export type DeliverySummaryCategory = DeliveryCounts & {
  eventId: string;
  /** Current event title; for an event no longer planned that day, the title its tickets were sold under. */
  title: string;
  titleSource?: 'event' | 'ticket' | 'id';
  /** False: the category is not in the day's current plan, so nothing can be sent for it. */
  eventFound?: boolean;
  offerQrCode: boolean;
  unavailableReasons?: UnavailableReasons;
};
export type DeliverySummary = DeliveryCounts & { unavailableReasons?: UnavailableReasons; appActive?: boolean; categories: DeliverySummaryCategory[] };

export type DoorGroup = { id: string; title: string; eventIds: string[]; includeNamedReservations: boolean; occassions: string[]; lockedAt?: number };
export type DoorDay = { date: string; revision: number; groups: DoorGroup[]; events: { eventId: string; title: string; occassions: string[] }[]; occassions: { id: string; title: string }[] };
export type BookingScope = { eventId: string; date: string; time: string; title: string };

export const doorApi = {
  deliverySummary: (restaurantId: string, eventId: string, date: string) => server.get<DeliverySummary>(`${base(restaurantId)}/delivery-summary/${encodeURIComponent(eventId)}/${date}`, { expectedErrorStatuses: EXPECTED }).then((r) => r.data),
  groups: (restaurantId: string, date: string) => server.get<DoorDay>(`${base(restaurantId)}/groups/${date}`, { expectedErrorStatuses: EXPECTED }).then((r) => r.data),
  saveGroup: (restaurantId: string, date: string, group: Omit<DoorGroup, 'id'> & { id?: string; expectedRevision: number }) => server.put(`${base(restaurantId)}/groups/${date}`, group, { expectedErrorStatuses: EXPECTED }).then((r) => r.data),
  removeGroup: (restaurantId: string, date: string, id: string, expectedRevision: number) => server.delete(`${base(restaurantId)}/groups/${date}/${id}`, { data: { expectedRevision }, expectedErrorStatuses: EXPECTED }).then((r) => r.data),
  bookingScope: (restaurantId: string, reservationId: string) => server.get<{ scope: BookingScope | null }>(`${base(restaurantId)}/booking-scope/${encodeURIComponent(reservationId)}`, { expectedErrorStatuses: EXPECTED }).then((r) => r.data.scope),
  me: (restaurantId: string) =>
    server.get<DoorMe>(`${base(restaurantId)}/me`, { expectedErrorStatuses: EXPECTED }).then((r) => r.data),

  events: (restaurantId: string) =>
    server
      .get<{ events: DoorEventOption[] }>(`${base(restaurantId)}/events`, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data.events),

  /** The picker's list: yesterday … +60 days, or with `before` the 30 older whole days before it. */
  eventPage: (restaurantId: string, before?: string, date?: string) =>
    server
      .get<DoorEventPage>(`${base(restaurantId)}/events${date ? `?date=${encodeURIComponent(date)}` : before ? `?before=${encodeURIComponent(before)}` : ''}`, {
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  closedEvents: (restaurantId: string) =>
    server
      .get<{ events: ClosedEvent[] }>(`${base(restaurantId)}/entry-controls/closed`, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data.events),

  setEntryClosed: (restaurantId: string, eventId: string, date: string, closed: boolean) =>
    server
      .put<{ control: { eventId: string; date: string; title: string; closed: boolean; changed: boolean } }>(
        `${base(restaurantId)}/entry-controls/${encodeURIComponent(eventId)}/${encodeURIComponent(date)}`,
        { closed },
        { expectedErrorStatuses: EXPECTED }
      )
      .then((r) => r.data.control),

  openSession: (
    restaurantId: string,
    body: { eventId: string; date: string; signature: string; pin?: string }
  ) =>
    server
      .post<{ session: DoorSession; permissions: DoorPermissions; eventTitle: string }>(
        `${base(restaurantId)}/sessions`,
        body,
        { expectedErrorStatuses: EXPECTED }
      )
      .then((r) => r.data),

  list: (session: DoorSession, q = '') =>
    server
      .get<DoorList>(`${base(session.restaurantId)}/list`, {
        params: { q },
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  view: (session: DoorSession, reservationId: string) =>
    server
      .get<{ view: DoorView }>(`${base(session.restaurantId)}/reservations/${encodeURIComponent(reservationId)}`, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data.view),

  scan: (session: DoorSession, code: string) =>
    server
      .post<{ view: DoorView }>(`${base(session.restaurantId)}/scan`, { code }, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data.view),

  admit: (
    session: DoorSession,
    body: {
      reservationId: string;
      quantity?: number;
      allRemaining?: boolean;
      origin?: 'table-plan';
      operationId: string;
      decision?: 'standard' | 'unpaid' | 'payment';
      reason?: string;
      payment?: { amountCents: number; method: 'cash' | 'card_terminal'; reference?: string };
      expectedOutstandingCents?: number;
    }
  ) =>
    server
      .post<{ result: string; quantity?: number; doorEventId: string; replayed: boolean; view: DoorView }>(
        `${base(session.restaurantId)}/admissions`,
        body,
        { headers: headers(session), expectedErrorStatuses: EXPECTED }
      )
      .then((r) => r.data),

  deny: (session: DoorSession, body: { reservationId: string; operationId: string; reason?: string }) =>
    server
      .post<{ result: string; view: DoorView }>(`${base(session.restaurantId)}/denials`, body, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  undo: (session: DoorSession, body: { reservationId: string; admissionId: string; operationId: string; origin?: 'table-plan' }) =>
    server
      .post<{ result: string; view: DoorView }>(`${base(session.restaurantId)}/undo`, body, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  reconcile: (
    session: DoorSession,
    body: {
      reservationId: string;
      quantity: number;
      operationId: string;
      actualAdmissionAt: string;
      note: string;
      decision?: 'standard' | 'unpaid';
      expectedOutstandingCents?: number;
    }
  ) =>
    server
      .post<{ result: string; replayed: boolean; view: DoorView }>(`${base(session.restaurantId)}/reconciliations`, body, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  releaseLegacy: (session: DoorSession, body: { reservationId: string; operationId: string; reason: string }) =>
    server
      .post<{ result: string; replayed: boolean; view: DoorView }>(`${base(session.restaurantId)}/legacy-release`, body, {
        headers: headers(session),
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),

  staff: (restaurantId: string) =>
    server
      .get<{ staff: StaffDoorRights[] }>(`${base(restaurantId)}/staff`, { expectedErrorStatuses: EXPECTED })
      .then((r) => r.data.staff),

  setStaffRights: (restaurantId: string, uid: string, rights: StaffDoorRights['ticketDoor']) =>
    server
      .put<{ ticketDoor: StaffDoorRights['ticketDoor'] }>(
        `${base(restaurantId)}/staff/${encodeURIComponent(uid)}/door-rights`,
        rights,
        { expectedErrorStatuses: EXPECTED }
      )
      .then((r) => r.data.ticketDoor),

  deliveries: (session: DoorSession) =>
    server
      .get<DeliveryOverview>(
        `${base(session.restaurantId)}/deliveries`,
        { headers: headers(session), expectedErrorStatuses: EXPECTED }
      )
      .then((r) => r.data),

  deliver: (session: DoorSession, body: { mode: 'backfill' | 'retry'; reservationIds: string[]; operationId: string }) =>
    server
      .post<{ results: DeliveryResult[] }>(`${base(session.restaurantId)}/deliveries/send`, body, {
        headers: headers(session),
        timeout: 120000,
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data.results),

  ticketPdf: (session: DoorSession, reservationId: string) =>
    server
      .get<Blob>(`${base(session.restaurantId)}/deliveries/${encodeURIComponent(reservationId)}/pdf`, {
        headers: headers(session),
        responseType: 'blob',
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data)
      .catch(async (error) => {
        throw await withReadableBody(error);
      }),

  exportCsv: (session: DoorSession) =>
    server
      .get<string>(`${base(session.restaurantId)}/export.csv`, {
        headers: headers(session),
        responseType: 'text',
        expectedErrorStatuses: EXPECTED,
      })
      .then((r) => r.data),
};
